import { useCallback, useMemo, useState } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { DateRangeFilterValue } from '@/components/ui/tableFilterFns';

const EMPTY_DATE_RANGE: DateRangeFilterValue = { from: null, to: null };

/** URL query param names for a date-range-filterable column's from/to bounds. */
export const dateRangeParamNames = (columnId: string) => ({
  from: `${columnId}_from`,
  to: `${columnId}_to`,
});

const readDateRangeFiltersFromParams = (
  searchParams: URLSearchParams,
  dateRangeColumns: string[]
): ColumnFiltersState =>
  dateRangeColumns.reduce<ColumnFiltersState>((acc, columnId) => {
    const { from, to } = dateRangeParamNames(columnId);
    const value = {
      from: searchParams.get(from) || null,
      to: searchParams.get(to) || null,
    };
    if (value.from || value.to) acc.push({ id: columnId, value });
    return acc;
  }, []);

interface UseDateRangeColumnFiltersArgs {
  /** Column ids with a DateRangeFilter — also the keys used for `_from`/`_to` URL params. */
  dateRangeColumns: string[];
  /** Read once on mount to restore filter state from a shared/bookmarked URL. */
  searchParams: URLSearchParams;
}

/**
 * Shared TanStack `columnFilters` state for date-range-filtered columns (the
 * pattern Studies and Sites both use), plus the read/apply helpers needed to
 * keep those filters in sync with the URL. `columnFilters` is intentionally
 * the general TanStack shape (not date-range-specific) so it can be passed
 * straight to `DataTable`'s `columnFilters`/`onColumnFiltersChange` props —
 * a page can still push non-date-range filters into the same state if needed.
 */
export const useDateRangeColumnFilters = ({
  dateRangeColumns,
  searchParams,
}: UseDateRangeColumnFiltersArgs) => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(() =>
    readDateRangeFiltersFromParams(searchParams, dateRangeColumns)
  );

  const dateRangeValue = useCallback(
    (columnId: string): DateRangeFilterValue =>
      (columnFilters.find(f => f.id === columnId)
        ?.value as DateRangeFilterValue) ?? EMPTY_DATE_RANGE,
    [columnFilters]
  );

  const setDateRangeValue = useCallback(
    (columnId: string) => (value: DateRangeFilterValue) => {
      setColumnFilters(prev => {
        const withoutColumn = prev.filter(f => f.id !== columnId);
        if (!value.from && !value.to) return withoutColumn;
        return [...withoutColumn, { id: columnId, value }];
      });
    },
    []
  );

  // Flat { paramName: value } map for every date-range column's from/to —
  // callers fold this into their own combined URLSearchParams sync effect
  // (alongside search/status/other filters) via their existing `apply(key,
  // value)` helper, the same way each page already applies its other params.
  const dateRangeUrlParams = useMemo(() => {
    const params: Record<string, string> = {};
    dateRangeColumns.forEach(columnId => {
      const { from, to } = dateRangeParamNames(columnId);
      const range = dateRangeValue(columnId);
      params[from] = range.from || '';
      params[to] = range.to || '';
    });
    return params;
  }, [dateRangeColumns, dateRangeValue]);

  return {
    columnFilters,
    setColumnFilters,
    dateRangeValue,
    setDateRangeValue,
    dateRangeUrlParams,
  };
};
